// Filename: Thumbnail.js

import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import './Thumbnail.css';
import ThumbnailPreview from './Thumbnail/ThumbnailPreview';
import ThumbnailControls from './Thumbnail/ThumbnailControls';
import PresetTemplates from './Thumbnail/PresetTemplates';
import EffectControls from './Thumbnail/EffectControls';
import { presets, effectParticles } from './Thumbnail/thumbnailConstants';
import { 
    compressImage, 
    resizeImageToFit, 
    hexToRgb, 
    hexToRgba 
} from './Thumbnail/thumbnailUtils';
import { recordPageVisit } from './utils/pageVisitTracker';
import html2canvas from 'html2canvas';


const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';
const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
const isMobileDevice = isMobile();

function Thumbnail({ language, user, countryCode, userIp }) {
    // 상태 관리
    const [settings, setSettings] = useState({
        mainTitle: '그대 먼 곳에',
        subTitle: '마음과 마음',
        artist: '김복희 & 임석범',
        mainIcon: '🎵',
        bgColor1: '#ff6b6b',
        bgColor2: '#4ecdc4',
        textColor: '#ffffff',
        bgType: 'gradient',
        fontSize: 40,
        textShadow: 3,
        bgOpacity: 70,
        effectSpeed: 1.0
    });
    
    const [currentEffect, setCurrentEffect] = useState('none');
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [effectInterval, setEffectInterval] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isEffectPaused, setIsEffectPaused] = useState(false);

    
    const canvasRef = useRef(null);
    const animationCanvasRef = useRef(null);
    const effectsLayerRef = useRef(null);

    const userId = user ? user.uid : 'unknown';

    useEffect(() => {
        // 페이지 방문 기록
        if (countryCode && countryCode !== 'UNKNOWN') {
            recordPageVisit('Thumbnail', {
                userId,
                userEmail: user ? user.email : 'unknown',
                userIp,
                countryCode,
                SERVER_ROOT,
                language,
                isMobileDevice
            });
        }
    }, [countryCode]);

    // 설정 업데이트 함수
    const updateSettings = (newSettings) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
    };

    const toggleEffectPause = () => {
        const effectsLayer = effectsLayerRef.current;
        if (!effectsLayer) return;

        const particles = effectsLayer.querySelectorAll('.particle');
        particles.forEach(p => {
            p.style.animationPlayState = isEffectPaused ? 'running' : 'paused';
        });

        setIsEffectPaused(!isEffectPaused);
    };


    // 프리셋 적용
    const applyPreset = (presetName) => {
        const preset = presets[presetName];
        if (preset) {
            updateSettings({
                bgColor1: preset.bgColor1,
                bgColor2: preset.bgColor2,
                textColor: preset.textColor,
                bgType: preset.bgType,
                mainIcon: preset.icon
            });
            toggleEffect(preset.effect);
        }
    };

    // 이펙트 토글
    const toggleEffect = (effect) => {
        setCurrentEffect(effect);
        clearInterval(effectInterval);
        
        const effectsLayer = effectsLayerRef.current;
        if (effectsLayer) {
            effectsLayer.innerHTML = '';
            effectsLayer.className = 'effects-layer';
            
            if (effect !== 'none') {
                effectsLayer.classList.add(effect);
                createParticles(effect);
                
                const interval = setInterval(() => {
                    if (effectsLayer.children.length < 15) {
                        createParticles(effect);
                    }
                }, 1000 / settings.effectSpeed);
                
                setEffectInterval(interval);
            }
        }
    };

    // 파티클 생성
    const createParticles = (effect) => {
        const effectsLayer = effectsLayerRef.current;
        if (!effectsLayer) return;
        
        const particles = effectParticles[effect] || ['•'];
        
        for (let i = 0; i < 4; i++) {
            const particle = document.createElement('div');
            particle.className = 'particle';
            
            particle.style.left = (Math.random() * 90) + '%';
            particle.style.top = '-50px';
            
            const delay = Math.random() * 2;
            particle.style.animationDelay = delay + 's';
            
            // 각 이펙트별 스타일 설정
            switch (effect) {
                case 'bubbles':
                    const size = 8 + Math.random() * 12;
                    particle.style.width = size + 'px';
                    particle.style.height = size + 'px';
                    particle.style.borderRadius = '50%';
                    particle.style.background = 'rgba(255, 255, 255, 0.4)';
                    particle.style.border = '1px solid rgba(173, 216, 230, 0.6)';
                    particle.style.animation = `floatUp ${(3 + Math.random() * 2) / settings.effectSpeed}s ease-in-out infinite`;
                    break;
                    
                case 'raindrops':
                    particle.style.width = '2px';
                    particle.style.height = '20px';
                    particle.style.background = 'linear-gradient(180deg, transparent, rgba(173, 216, 230, 0.8))';
                    particle.style.animation = `fallDown ${(1.5 + Math.random() * 0.5) / settings.effectSpeed}s linear infinite`;
                    break;
                    
                case 'rainlines':
                    particle.style.width = '1px';
                    particle.style.height = (30 + Math.random() * 20) + 'px';
                    particle.style.background = 'linear-gradient(180deg, transparent, rgba(173, 216, 230, 0.7), transparent)';
                    particle.style.animation = `rainLines ${(0.6 + Math.random() * 0.4) / settings.effectSpeed}s linear infinite`;
                    particle.style.left = (Math.random() * 100) + '%';
                    break;
                    
                case 'snowflakes':
                    particle.textContent = particles[Math.floor(Math.random() * particles.length)];
                    particle.style.fontSize = (14 + Math.random() * 10) + 'px';
                    particle.style.color = 'white';
                    particle.style.textShadow = '0 0 10px rgba(255,255,255,0.8)';
                    particle.style.animation = `zigzag ${(4 + Math.random() * 2) / settings.effectSpeed}s linear infinite`;
                    break;
                    
                case 'leaves':
                    particle.textContent = particles[Math.floor(Math.random() * particles.length)];
                    particle.style.fontSize = (14 + Math.random() * 8) + 'px';
                    particle.style.animation = `drift ${(5 + Math.random() * 2) / settings.effectSpeed}s linear infinite`;
                    break;
                    
                case 'flowers':
                    particle.textContent = particles[Math.floor(Math.random() * particles.length)];
                    particle.style.fontSize = (12 + Math.random() * 6) + 'px';
                    particle.style.animation = `drift ${(4 + Math.random() * 2) / settings.effectSpeed}s ease-in-out infinite`;
                    break;
                    
                case 'hearts':
                    particle.textContent = particles[Math.floor(Math.random() * particles.length)];
                    particle.style.fontSize = (10 + Math.random() * 6) + 'px';
                    particle.style.color = '#FF69B4';
                    particle.style.textShadow = '0 0 8px rgba(255,105,180,0.6)';
                    particle.style.animation = `twinkle ${(3 + Math.random() * 1.5) / settings.effectSpeed}s ease-in-out infinite`;
                    particle.style.left = (20 + Math.random() * 60) + '%';
                    particle.style.top = (50 + Math.random() * 100) + 'px';
                    break;
                    
                case 'stars':
                    particle.textContent = particles[Math.floor(Math.random() * particles.length)];
                    particle.style.fontSize = (8 + Math.random() * 8) + 'px';
                    particle.style.color = '#FFD700';
                    particle.style.textShadow = '0 0 12px rgba(255,215,0,0.8)';
                    particle.style.animation = `twinkle ${(4 + Math.random() * 2) / settings.effectSpeed}s ease-in-out infinite`;
                    particle.style.left = (Math.random() * 85) + '%';
                    particle.style.top = (20 + Math.random() * 200) + 'px';
                    break;
                    
                default:
                    break;
            }
            
            effectsLayer.appendChild(particle);
            
            setTimeout(() => {
                if (particle.parentNode) {
                    particle.parentNode.removeChild(particle);
                }
            }, 8000 / settings.effectSpeed);
        }
    };

    // 이미지 업로드 처리
    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = function(e) {
                const img = new Image();
                img.onload = function() {
                    setBackgroundImage(resizeImageToFit(img, 600, 315));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    // 드래그 앤 드랍 처리
    const handleDragOver = (event) => {
        event.preventDefault();
        event.currentTarget.classList.add('dragover');
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.currentTarget.classList.remove('dragover');
        
        const files = event.dataTransfer.files;
        if (files.length > 0 && files[0].type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = function(e) {
                const img = new Image();
                img.onload = function() {
                    setBackgroundImage(resizeImageToFit(img, 600, 315));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(files[0]);
        }
    };

    // 썸네일 다운로드
    const downloadThumbnail = async () => {
        const previewElement = document.querySelector('.thumbnail-preview'); // ThumbnailPreview의 최상위 클래스

        if (!previewElement) {
            alert("미리보기를 찾을 수 없습니다.");
            return;
        }

        try {
            const canvas = await html2canvas(previewElement, {
                useCORS: true,
                scale: 2 // 고해상도 출력
            });
            const image = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = image;
            link.download = `${settings.mainTitle}_thumbnail.png`;
            link.click();
        } catch (error) {
            console.error("다운로드 중 오류 발생:", error);
            alert("이미지를 저장하는 중 오류가 발생했습니다.");
        }
    };


    // 배경 그리기
    const drawBackground = (ctx) => {
        if (backgroundImage) {
            const img = new Image();
            img.onload = function() {
                ctx.drawImage(img, 0, 0, 600, 315);
                
                const bgOpacity = settings.bgOpacity / 100;
                const gradient = ctx.createLinearGradient(0, 0, 600, 315);
                gradient.addColorStop(0, hexToRgba(settings.bgColor1, bgOpacity));
                gradient.addColorStop(1, hexToRgba(settings.bgColor2, bgOpacity));
                ctx.fillStyle = gradient;
                ctx.fillRect(0, 0, 600, 315);
            };
            img.src = backgroundImage;
        } else {
            if (settings.bgType === 'gradient') {
                const gradient = ctx.createLinearGradient(0, 0, 600, 315);
                gradient.addColorStop(0, settings.bgColor1);
                gradient.addColorStop(1, settings.bgColor2);
                ctx.fillStyle = gradient;
            } else if (settings.bgType === 'radial') {
                const gradient = ctx.createRadialGradient(300, 157, 0, 300, 157, 300);
                gradient.addColorStop(0, settings.bgColor1);
                gradient.addColorStop(1, settings.bgColor2);
                ctx.fillStyle = gradient;
            } else {
                ctx.fillStyle = settings.bgColor1;
            }
            ctx.fillRect(0, 0, 600, 315);
        }
    };

    // 텍스트 그리기
    const drawText = (ctx) => {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
        ctx.shadowBlur = settings.textShadow * 2;
        ctx.shadowOffsetX = settings.textShadow;
        ctx.shadowOffsetY = settings.textShadow;

        ctx.fillStyle = settings.textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // 이모지
        ctx.font = '48px Arial';
        ctx.fillText(settings.mainIcon, 300, 80);
        
        // 메인 제목
        ctx.font = `bold ${settings.fontSize}px Arial`;
        ctx.fillText(settings.mainTitle, 300, 140);
        
        // 부제목
        ctx.font = '24px Arial';
        ctx.fillText(settings.subTitle, 300, 180);
        
        // 아티스트
        ctx.font = '18px Arial';
        ctx.fillText(settings.artist, 300, 220);
    };

    
    // GIF 다운로드 (서버 API 사용)
    const downloadGif = async () => {
        setIsGenerating(true);
        
        try {
            const response = await fetch(`${SERVER_ROOT}/api/generate-gif`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    settings,
                    currentEffect,
                    backgroundImage,
                    userId: user ? user.uid : 'anonymous'
                }),
            });
            
            if (!response.ok) {
                throw new Error('GIF 생성 실패');
            }
            
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${settings.mainTitle}_animation.gif`;
            link.click();
            URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('GIF 생성 중 오류:', error);
            alert('GIF 생성 중 오류가 발생했습니다.');
        } finally {
            setIsGenerating(false);
        }
    };

    // 애니메이션 프레임 렌더링
    const renderAnimationFrame = (ctx, progress) => {
        const canvas = ctx.canvas;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // 배경 그리기
        drawBackground(ctx);
        
        // 파티클 렌더링
        renderParticles(ctx, progress);
        
        // 텍스트 그리기
        drawText(ctx);
    };

    // 파티클 렌더링 (애니메이션용)
    const renderParticles = (ctx, progress) => {
        if (currentEffect === 'none') return;
        
        const particles = effectParticles[currentEffect] || ['•'];
        const particleCount = 15;
        
        for (let i = 0; i < particleCount; i++) {
            const particleProgress = (progress * 2 + i / particleCount) % 1;
            const baseX = (i / particleCount) * 600;
            const wobbleX = Math.sin(progress * Math.PI * 2 + i * 0.5) * 30;
            const x = (baseX + wobbleX) % 600;
            
            // 각 이펙트별 파티클 렌더링 로직
            switch (currentEffect) {
                case 'bubbles':
                    const y = 315 - (particleProgress * 400);
                    const size = 4 + Math.sin(progress * Math.PI * 3 + i) * 3;
                    const alpha = Math.max(0, 1 - particleProgress);
                    
                    ctx.save();
                    ctx.globalAlpha = alpha * 0.7;
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                    ctx.strokeStyle = 'rgba(173, 216, 230, 0.8)';
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.arc(x, y, size, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.stroke();
                    ctx.restore();
                    break;
                    
                // 다른 이펙트들도 비슷하게 구현...
                default:
                    break;
            }
        }
    };

    

    // 초기화
    const resetAll = () => {
        setSettings({
            mainTitle: '그대 먼 곳에',
            subTitle: '마음과 마음',
            artist: '김복희 & 임석범',
            mainIcon: '🎵',
            bgColor1: '#ff6b6b',
            bgColor2: '#4ecdc4',
            textColor: '#ffffff',
            bgType: 'gradient',
            fontSize: 40,
            textShadow: 3,
            bgOpacity: 70,
            effectSpeed: 1.0
        });
        setBackgroundImage(null);
        toggleEffect('none');
    };

    // 컴포넌트 언마운트 시 정리
    useEffect(() => {
        return () => {
            if (effectInterval) {
                clearInterval(effectInterval);
            }
        };
    }, [effectInterval]);

    return (
        <div className="thumbnail-container">
            <Helmet>
                <title>감성 블로그 대표이미지 생성기 | Doldari</title>
                <meta name="description" content="아름다운 효과와 배경으로 감성적인 썸네일을 만들어보세요!" />
                <meta name="keywords" content="thumbnail, blog, image, generator, 썸네일, 블로그, 이미지" />
            </Helmet>

            <div className="container" >
                <h1>🎨✨ 감성 블로그 대표이미지 생성기</h1>
                <p>아름다운 효과와 배경으로 감성적인 썸네일을 만들어보세요!</p>
                <div style={{
                    display: 'flex', 
                    flexDirection: isMobileDevice ? 'column' : 'row', 
                    gap: '20px'
                    }}>
                    <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                        <ThumbnailPreview 
                            settings={settings}
                            backgroundImage={backgroundImage}
                            effectsLayerRef={effectsLayerRef}
                            language={language}
                        />

                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <button className="download-btn" onClick={downloadThumbnail}>
                                📥 이미지 다운로드
                            </button>

                            <button 
                            className="download-btn" 
                            onClick={toggleEffectPause} 
                            style={{ background: isEffectPaused ? '#FFC107' : '#607D8B' }}
                            >
                            {isEffectPaused ? '▶️ 이펙트 재생' : '⏸️ 이펙트 정지'}
                            </button>

                            
                            <button 
                                className="download-btn" 
                                onClick={downloadGif} 
                                style={{ background: '#9C27B0' }}
                                disabled={isGenerating}
                            >
                                {isGenerating ? '🎞️ 생성 중...' : '🎞️ 애니메이션 GIF 다운로드'}
                            </button>
                            
                            <button 
                                className="download-btn" 
                                onClick={resetAll} 
                                style={{ background: '#9E9E9E' }}
                            >
                                🔄 초기화
                            </button>
                        </div>

                        <div className="tip">
                            <h4 style={{textAlign: 'left'}}>💡 사용 팁</h4>
                            <ul style={{textAlign: 'left', paddingLeft: '30px'}}>
                                <li><strong>감성 효과:</strong> 콘텐츠 분위기에 맞는 효과를 선택하세요</li>
                                <li><strong>배경 이미지:</strong> 저작권 없는 이미지만 사용하세요</li>
                                <li><strong>가독성:</strong> 텍스트가 잘 보이도록 배경 투명도와 그림자를 조절하세요</li>
                                <li><strong>일관성:</strong> 블로그 전체적으로 비슷한 스타일을 유지하면 브랜딩에 도움됩니다</li>
                            </ul>
                        </div>
                    </div>
                    
                    <div>
                        <PresetTemplates onApplyPreset={applyPreset} language={language} />
                        
                        <div className="control-section">
                            <h3>🖼️ 배경 이미지</h3>
                            <div 
                                className="image-upload" 
                                onClick={() => document.getElementById('bgImageInput').click()}
                                onDragOver={handleDragOver} 
                                onDrop={handleDrop}
                            >
                                <p>📁 클릭하거나 이미지를 드래그해서 배경 추가 (JPG, PNG 파일 지원)</p>
                            </div>
                            <input 
                                type="file" 
                                id="bgImageInput" 
                                accept="image/*" 
                                style={{ display: 'none' }} 
                                onChange={handleImageUpload} 
                            />
                        </div>

                        <EffectControls 
                            currentEffect={currentEffect}
                            onToggleEffect={toggleEffect}
                            language={language}
                        />

                        <ThumbnailControls 
                            settings={settings}
                            onUpdateSettings={updateSettings}
                            language={language}
                        />
                    </div>

                    
                </div>
            </div>

            {/* 숨겨진 캔버스들 */}
            <canvas ref={canvasRef} width="600" height="315" style={{ display: 'none' }}></canvas>
            <canvas ref={animationCanvasRef} width="600" height="315" style={{ display: 'none' }}></canvas>
        </div>
    );
}

export default Thumbnail;